import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { PaymentSetupPage } from './pages/PaymentSetupPage';
import { JournalPage } from './pages/JournalPage';
import { SettingsPage } from './pages/SettingsPage';
import { AdminLayout } from './pages/admin/AdminLayout';
import { PrivacyPolicy } from './pages/legal/PrivacyPolicy';
import { TermsOfService } from './pages/legal/TermsOfService';
import { useUserProgress } from './hooks/useUserProgress';
import { useSubscription } from './hooks/useSubscription';
import { isPreviewMode } from './services/supabase';
import { PublicLayout } from './layouts/PublicLayout';
import { ProtectedLayout } from './layouts/ProtectedLayout';
import { isPublicRoute } from './constants/routes';
import { LandingPage } from './pages/LandingPage';
import { FeaturesPage } from './pages/FeaturesPage';
import { AboutPage } from './pages/AboutPage';
import { PricingPage } from './pages/PricingPage';
import { SignupPage } from './pages/SignupPage';
import { LoginPage } from './pages/LoginPage';
import { CoachPage } from './pages/CoachPage';
import { AppDashboard } from './pages/AppDashboard';
import { HeadspaceAlternativePage } from './pages/compare/HeadspaceAlternativePage';
import { CalmAlternativePage } from './pages/compare/CalmAlternativePage';
import { BetterHelpAlternativePage } from './pages/compare/BetterHelpAlternativePage';
import { TextMeiAlternativePage } from './pages/compare/TextMeiAlternativePage';
import { LifeCoachAIAlternativePage } from './pages/compare/LifeCoachAIAlternativePage';
import { FreeAILifeCoachPage } from './pages/compare/FreeAILifeCoachPage';
import { BestAILifeCoachPage } from './pages/compare/BestAILifeCoachPage';
import { CareerChangeCoachPage } from './pages/usecases/CareerChangeCoachPage';
import { ImpostorSyndromeCoachPage } from './pages/usecases/ImpostorSyndromeCoachPage';
import { RelationshipCoachPage } from './pages/usecases/RelationshipCoachPage';
import { AICareerCoachPage } from './pages/usecases/AICareerCoachPage';
import { AnxietyCoachPage } from './pages/usecases/AnxietyCoachPage';
import { WorkLifeBalanceCoachPage } from './pages/usecases/WorkLifeBalanceCoachPage';
import { BlogPage } from './pages/blog/BlogPage';
import { BlogPostPage } from './pages/blog/BlogPostPage';
import { InvitePage } from './pages/InvitePage';
import { UnsubscribePage } from './pages/UnsubscribePage';
import { CareerClarityJournalPage } from './pages/CareerClarityJournalPage';
import { OverthinkingJournalPage } from './pages/OverthinkingJournalPage';
import { StrawberryMeAlternativePage } from './pages/compare/StrawberryMeAlternativePage';
import { CreatorsPage } from './pages/CreatorsPage';

function App() {
  const { updateProgress } = useUserProgress();
  const navigate = useNavigate();
  const location = useLocation();
  const [initialized, setInitialized] = useState(false);
  const { 
    needsPayment,
    refresh: refreshSubscription
  } = useSubscription();

  // Check if payment method is required
  const checkPaymentRequired = useCallback(async () => {
    if (isPreviewMode()) return;

    // Skip payment check for public routes
    if (isPublicRoute(location.pathname)) return;
    
    // Skip for payment setup route
    const isPaymentSetupRoute = location.pathname === '/payment-setup';

    if (!isPaymentSetupRoute && needsPayment) {
      navigate('/payment-setup');
    }
  }, [location.pathname, needsPayment, navigate]);

  useEffect(() => {
    setInitialized(true);
    checkPaymentRequired();
  }, [checkPaymentRequired]);

  // Update progress when location changes, but only for non-public routes
  useEffect(() => {
    if (updateProgress && !isPublicRoute(location.pathname)) {
      updateProgress({ lastVisitedRoute: location.pathname });
    }
  }, [location.pathname, updateProgress]);

  // Listen for auth refresh events
  useEffect(() => {
    const handleAuthRefresh = () => {
      refreshSubscription();
      checkPaymentRequired();
    };

    window.addEventListener('supabase-auth-refresh', handleAuthRefresh);
    return () => window.removeEventListener('supabase-auth-refresh', handleAuthRefresh);
  }, [refreshSubscription, checkPaymentRequired]);

  if (!initialized) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes with navbar/footer - critical for signup and payment flow */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<LandingPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/compare/headspace-alternative" element={<HeadspaceAlternativePage />} />
        <Route path="/compare/calm-alternative" element={<CalmAlternativePage />} />
        <Route path="/compare/betterhelp-alternative" element={<BetterHelpAlternativePage />} />
        <Route path="/compare/textmei-alternative" element={<TextMeiAlternativePage />} />
        <Route path="/compare/lifecoachai-alternative" element={<LifeCoachAIAlternativePage />} />
        <Route path="/compare/free-ai-life-coach" element={<FreeAILifeCoachPage />} />
        <Route path="/compare/best-ai-life-coach" element={<BestAILifeCoachPage />} />
        <Route path="/compare/strawberry-me-alternative" element={<StrawberryMeAlternativePage />} />
        <Route path="/creators" element={<CreatorsPage />} />
        <Route path="/coach/career-change" element={<CareerChangeCoachPage />} />
        <Route path="/coach/impostor-syndrome" element={<ImpostorSyndromeCoachPage />} />
        <Route path="/coach/relationship-coach" element={<RelationshipCoachPage />} />
        <Route path="/coach/ai-career-coach" element={<AICareerCoachPage />} />
        <Route path="/coach/anxiety" element={<AnxietyCoachPage />} />
        <Route path="/coach/work-life-balance" element={<WorkLifeBalanceCoachPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/career-clarity-journal" element={<CareerClarityJournalPage />} />
        <Route path="/overthinking-journal" element={<OverthinkingJournalPage />} />
        <Route path="/legal/privacy" element={<PrivacyPolicy />} />
        <Route path="/legal/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />

        {/* Auth routes - critical for user acquisition */}
        <Route path="/signin" element={<LoginPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
      </Route>
      
      {/* Payment setup - standalone for smooth payment flow */}
      <Route path="/payment-setup" element={<PaymentSetupPage />} />

      {/* Unsubscribe - standalone for email links */}
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
      
      {/* Protected routes */}
      <Route element={<ProtectedLayout />}>
        <Route path="/app" element={<AppDashboard />} />
        <Route path="/journal" element={<JournalPage />} />
        <Route path="/coach" element={<CoachPage />} />
        <Route path="/plans" element={<Navigate to="/coach" replace />} />
        <Route path="/prompts" element={<Navigate to="/coach" replace />} />
        <Route path="/scorecard" element={<Navigate to="/app" replace />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/invite" element={<InvitePage />} />
        <Route path="/dashboard" element={<Navigate to="/app" replace />} />
        <Route path="/dashboard/*" element={<Navigate to="/app" replace />} />
      </Route>
      
      {/* Admin routes */}
      <Route path="/admin/*" element={<AdminLayout />} />
      
      {/* Catch-all route - redirects to home for SEO and user experience */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;